import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format, parse } from 'date-fns';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import type { Lesson } from '@court-sync/shared';
import { getLessonCoachIds } from '@court-sync/shared';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Checkbox, Field, FieldLabel, FieldError } from '@/components/ui';
import { useCoaches } from '@/hooks/useDataStore';
import { lessonOverrideService } from '@/services/lessonOverride';
import { lessonService } from '@/services/lesson';

type CoachScope = 'session' | 'lesson';

function getScopeFullLabel(lesson: Lesson): string {
  if (lesson.termId && (lesson.lessonType === 'group' || lesson.lessonType === 'private')) {
    return 'Entire term';
  }
  return 'All sessions of this lesson';
}

export interface AddCoachToSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  academyId: string;
  lesson: Lesson;
  sessionDateYmd: string;
  effectiveSessionId?: string;
  resolvedCoachIds: string[];
  stackLevel?: 'default' | 'elevated';
  onSuccess?: () => void | Promise<void>;
}

export function AddCoachToSessionModal({
  isOpen,
  onClose,
  academyId,
  lesson,
  sessionDateYmd,
  effectiveSessionId,
  resolvedCoachIds,
  stackLevel = 'elevated',
  onSuccess,
}: AddCoachToSessionModalProps) {
  const { coaches, isLoading: coachesLoading, refresh: refreshCoaches } = useCoaches(academyId);
  const sessionScopeAvailable = !!effectiveSessionId;
  const [coachScope, setCoachScope] = useState<CoachScope>(() =>
    effectiveSessionId ? 'session' : 'lesson'
  );
  const [selectedCoachId, setSelectedCoachId] = useState('');
  const [selectedCoachError, setSelectedCoachError] = useState('');
  const [reason, setReason] = useState('');
  const [notifyCoachEmail, setNotifyCoachEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const availableCoaches = useMemo(
    () => coaches.filter((c) => c.isActive && !resolvedCoachIds.includes(c.id)),
    [coaches, resolvedCoachIds]
  );

  const sessionDateLabel =
    sessionDateYmd && /^\d{4}-\d{2}-\d{2}$/.test(sessionDateYmd)
      ? format(parse(sessionDateYmd, 'yyyy-MM-dd', new Date()), 'd MMM yyyy')
      : null;

  const scopeFullLabel = getScopeFullLabel(lesson);

  useEffect(() => {
    if (!isOpen) return;
    setCoachScope(effectiveSessionId ? 'session' : 'lesson');
    setSelectedCoachId('');
    setSelectedCoachError('');
    setReason('');
    setNotifyCoachEmail(true);
    void refreshCoaches();
  }, [isOpen, effectiveSessionId, refreshCoaches]);

  const addCoachToTarget = useCallback(
    async (coachId: string) => {
      if (coachScope === 'session') {
        if (!effectiveSessionId) {
          throw new Error('A materialized session is required to add a coach for this session only.');
        }
        if (resolvedCoachIds.includes(coachId)) {
          throw new Error('This coach is already assigned to this session.');
        }
        await lessonOverrideService.replaceSessionCoaches({
          academyId,
          sessionId: effectiveSessionId,
          coachIds: [...resolvedCoachIds, coachId],
          reason: reason.trim() || undefined,
          notifyCoachEmail,
        });
        toast.success('Coach added to this session');
        return;
      }

      if (getLessonCoachIds(lesson).includes(coachId)) {
        throw new Error('This coach is already assigned to this lesson.');
      }
      await lessonService.addLessonCoach(lesson.id, { academyId, coachId, notifyCoachEmail });
      toast.success(`Coach added to ${scopeFullLabel.toLowerCase()}`);
    },
    [
      academyId,
      coachScope,
      effectiveSessionId,
      lesson,
      reason,
      resolvedCoachIds,
      scopeFullLabel,
      notifyCoachEmail,
    ]
  );

  const handleAddExisting = async () => {
    if (!selectedCoachId) {
      setSelectedCoachError('Select a coach');
      return;
    }
    setSelectedCoachError('');
    setSubmitting(true);
    try {
      await addCoachToTarget(selectedCoachId);
      await onSuccess?.();
      onClose();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to add coach');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add coach"
      maxWidth="lg"
      stackLevel={stackLevel}
    >
      <div className="space-y-4 text-sm">
        <p className="text-xs text-gray-600 dark:text-gray-400">
          Select an existing coach and assign them to this session or the whole lesson.
        </p>

        <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Assignment scope
          </p>
          {sessionDateLabel ? (
            <p className="text-xs text-muted-foreground">
              Session date: <span className="font-medium text-foreground">{sessionDateLabel}</span>
            </p>
          ) : null}
          <div className="flex flex-col gap-2">
            <label
              className={`flex items-start gap-2 text-sm ${
                sessionScopeAvailable ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
              }`}
            >
              <input
                type="radio"
                name={`coach-scope-${lesson.id}`}
                className="mt-0.5 text-primary"
                checked={coachScope === 'session'}
                disabled={submitting || !sessionScopeAvailable}
                onChange={() => setCoachScope('session')}
              />
              <span>This session only</span>
            </label>
            {!sessionScopeAvailable ? (
              <p className="text-xs text-amber-700 dark:text-amber-300">
                A materialized session record is required to customize the coach roster for this date.
                You can still add the coach to {scopeFullLabel.toLowerCase()}.
              </p>
            ) : null}
            <label className="flex cursor-pointer items-start gap-2 text-sm">
              <input
                type="radio"
                name={`coach-scope-${lesson.id}`}
                className="mt-0.5 text-primary"
                checked={coachScope === 'lesson'}
                disabled={submitting}
                onChange={() => setCoachScope('lesson')}
              />
              <span>{scopeFullLabel}</span>
            </label>
          </div>
          {coachScope === 'session' && sessionScopeAvailable ? (
            <Field>
              <FieldLabel htmlFor="add-coach-reason">Reason (optional)</FieldLabel>
              <textarea
                id="add-coach-reason"
                className="w-full rounded border border-gray-300 bg-white px-2 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="E.g., Extra cover for a busy session"
              />
            </Field>
          ) : null}
          {coachScope === 'session' && sessionScopeAvailable ? (
            <p className="text-xs text-gray-600 dark:text-gray-400">
              This creates a custom roster for this session only. Other dates keep the lesson&apos;s
              default coaches.
            </p>
          ) : null}
        </div>

        <Field data-invalid={!!selectedCoachError}>
          <FieldLabel htmlFor="add-coach-existing">Coach</FieldLabel>
          {coachesLoading ? (
            <div className="flex items-center gap-2 py-2 text-xs text-gray-500 dark:text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Loading coaches…
            </div>
          ) : (
            <select
              id="add-coach-existing"
              className={`w-full rounded border bg-white px-2 py-2 text-sm text-gray-900 dark:bg-gray-900 dark:text-gray-100 ${
                selectedCoachError
                  ? 'border-red-500 dark:border-red-500'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
              value={selectedCoachId}
              onChange={(e) => {
                setSelectedCoachId(e.target.value);
                if (selectedCoachError) setSelectedCoachError('');
              }}
            >
              <option value="">— Select coach —</option>
              {availableCoaches.map((coach) => (
                <option key={coach.id} value={coach.id}>
                  {coach.firstName} {coach.lastName}
                </option>
              ))}
            </select>
          )}
          {selectedCoachError ? <FieldError>{selectedCoachError}</FieldError> : null}
          {!coachesLoading && availableCoaches.length === 0 ? (
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
              No other active coaches are available. Add coaches in Staff management first.
            </p>
          ) : null}
        </Field>

        <div className="flex items-start gap-3">
          <Checkbox
            id="add-coach-notify-email"
            checked={notifyCoachEmail}
            onCheckedChange={(checked) =>
              setNotifyCoachEmail(typeof checked === 'boolean' ? checked : false)
            }
            disabled={submitting}
          />
          <label
            htmlFor="add-coach-notify-email"
            className="cursor-pointer text-sm text-gray-700 dark:text-gray-300"
          >
            Notify coach by email
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleAddExisting()}
            disabled={submitting || coachesLoading || availableCoaches.length === 0}
          >
            {submitting ? 'Adding…' : 'Add coach'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
